/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useCallback, useMemo } from '@web-stories-wp/react';
import {
  PAGE_HEIGHT,
  PAGE_WIDTH,
  UnitsProvider,
  useUnits,
} from '@web-stories-wp/units';

const NATIVE_PAGE_SIZE = Object.freeze({
  width: PAGE_WIDTH,
  height: PAGE_HEIGHT,
});

const Viewport = styled.div`
  position: absolute;
  inset: 0;
  overflow: visible;
`;

const NativeCanvas = styled.div.attrs(({ $scaleX, $scaleY }) => ({
  style: {
    width: $scaleX > 0 ? `${100 / $scaleX}%` : '100%',
    height: $scaleY > 0 ? `${100 / $scaleY}%` : '100%',
    transform: `scale(${$scaleX}, ${$scaleY})`,
  },
}))`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
`;

/**
 * Keeps text layout in the persisted page coordinate space while scaling the
 * completed result into the editor. This makes the browser shape text at the
 * same font size used by the final renderer instead of reshaping it at every
 * editor zoom level.
 */
function NativeTextLayout({ children }) {
  const { dataToEditorX, dataToEditorY, pageSize } = useUnits(
    ({ actions, state }) => ({
      dataToEditorX: actions.dataToEditorX,
      dataToEditorY: actions.dataToEditorY,
      pageSize: state.pageSize,
    })
  );
  const scaleX = pageSize.width / PAGE_WIDTH;
  const scaleY = pageSize.height / PAGE_HEIGHT;
  const toNativeX = useCallback(
    (value) => (scaleX > 0 ? value / scaleX : value),
    [scaleX]
  );
  const toNativeY = useCallback(
    (value) => (scaleY > 0 ? value / scaleY : value),
    [scaleY]
  );
  const outerUnits = useMemo(
    () => ({
      dataToEditorX,
      dataToEditorY,
      toNativeX,
      toNativeY,
    }),
    [dataToEditorX, dataToEditorY, toNativeX, toNativeY]
  );

  return (
    <Viewport data-testid="native-text-viewport">
      <NativeCanvas
        data-testid="native-text-canvas"
        $scaleX={scaleX}
        $scaleY={scaleY}
      >
        <UnitsProvider pageSize={NATIVE_PAGE_SIZE}>
          {children(outerUnits)}
        </UnitsProvider>
      </NativeCanvas>
    </Viewport>
  );
}

NativeTextLayout.propTypes = {
  children: PropTypes.func.isRequired,
};

export default NativeTextLayout;
